import { useEffect } from "react"
import { Form, Input, Modal, QRCode, Select } from "antd"
import { buildPublicQrCodeUrl } from "../helpers"
import { QrCodeFormData, QrCodeModalProps } from "../types"

interface InternalFormData {
  code?: string
  name: string
  content?: string
  fileAssetId?: number
  note?: string
}

const { TextArea } = Input

const QrCodeModal = ({
  isOpen,
  qrCode,
  loading,
  fileOptions,
  onClose,
  onSave,
}: QrCodeModalProps) => {
  const [form] = Form.useForm<InternalFormData>()
  const codeValue = Form.useWatch("code", form)
  const previewValue = codeValue ? buildPublicQrCodeUrl(codeValue) : ""

  const generateCode = () => {
    if (typeof crypto !== "undefined" && typeof crypto.randomUUID === "function") {
      return crypto.randomUUID().replace(/-/g, "").slice(0, 12)
    }

    return `${Date.now().toString(36)}${Math.random().toString(36).slice(2, 8)}`
  }

  useEffect(() => {
    if (isOpen && qrCode) {
      form.setFieldsValue({
        code: qrCode.code,
        name: qrCode.name,
        content: qrCode.content,
        fileAssetId: qrCode.fileAssetId ? Number(qrCode.fileAssetId) : undefined,
        note: qrCode.note || undefined,
      })
      return
    }

    if (isOpen) {
      form.resetFields()
      form.setFieldValue("code", generateCode())
    }
  }, [form, isOpen, qrCode])

  const handleSubmit = async () => {
    const values = await form.validateFields()
    const formData: QrCodeFormData = {
      code: values.code?.trim(),
      name: values.name.trim(),
      content: values.content?.trim(),
      fileAssetId: values.fileAssetId ?? null,
      note: values.note?.trim(),
    }
    onSave(formData)
  }

  return (
    <Modal
      title={qrCode ? "编辑二维码" : "新增二维码"}
      open={isOpen}
      onOk={handleSubmit}
      onCancel={onClose}
      confirmLoading={loading}
      width={760}
      destroyOnHidden
      okText="确定"
      cancelText="取消"
    >
      <div className="grid grid-cols-1 gap-6 md:grid-cols-[1fr_220px]">
        <Form
          form={form}
          layout="vertical"
          preserve={false}
        >
          <Form.Item name="code" hidden>
            <Input type="hidden" />
          </Form.Item>

          <Form.Item
            name="name"
            label="二维码名称"
            rules={[{ required: true, message: "请输入二维码名称" }]}
          >
            <Input placeholder="请输入二维码名称" maxLength={200} />
          </Form.Item>

          <Form.Item
            name="fileAssetId"
            label="绑定文件"
            extra="扫码入口保持不变，后续换文件只需要维护这里的绑定关系。"
          >
            <Select
              allowClear
              placeholder="请选择要绑定的文件（可选）"
              showSearch
              optionFilterProp="label"
              options={fileOptions.map((item) => ({
                value: Number(item.id),
                label: `${item.name} (${item.originalName})`,
              }))}
            />
          </Form.Item>

          <Form.Item
            name="content"
            label="备用内容"
            extra="没有绑定文件时，扫码入口会使用这里的 URL 或文本。"
          >
            <TextArea
              rows={4}
              placeholder="请输入备用 URL 或文本（可选）"
              maxLength={5000}
              showCount
            />
          </Form.Item>

          <Form.Item name="note" label="备注">
            <TextArea
              rows={3}
              placeholder="请输入备注信息（可选）"
              maxLength={1000}
              showCount
            />
          </Form.Item>
        </Form>

        <div className="flex flex-col items-center justify-start rounded-lg border border-dashed p-4">
          <div className="mb-3 text-sm text-gray-500">二维码预览</div>
          <QRCode value={previewValue || " "} size={180} />
          <div className="mt-3 break-all text-xs text-gray-500">
            {previewValue || "保存后生成稳定扫码入口"}
          </div>
        </div>
      </div>
    </Modal>
  )
}

export default QrCodeModal
